/**
 * AccountPage Component
 * Redirects to Shopify account or shows account options
 */

import { useEffect } from 'react';
import { generateStaticPageMetaTags, applyMetaTags } from '~/lib/seo/meta-tags';
import { motion } from 'motion/react';
import { Layout } from '~/components/layout/Layout';
import { IconUser, IconArrowRight } from '~/components/ui/Icons';

// Shopify store domain for account redirects
const SHOPIFY_STORE_DOMAIN = import.meta.env.VITE_SHOPIFY_STORE_DOMAIN;

export function AccountPage() {
  useEffect(() => {
    const meta = generateStaticPageMetaTags('account', 'Sign in to view your orders, manage your details, and track deliveries.');
    applyMetaTags({ ...meta, title: 'My Account | IT Remarketing' });
  }, []);

  const shopifyAccountUrl = `https://${SHOPIFY_STORE_DOMAIN}/account`;
  const shopifyLoginUrl = `https://${SHOPIFY_STORE_DOMAIN}/account/login`;
  const shopifyRegisterUrl = `https://${SHOPIFY_STORE_DOMAIN}/account/register`;

  return (
    <Layout>
      <div className="container-wide py-12 lg:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md mx-auto text-center"
        >
          {/* Icon */}
          <div className="w-20 h-20 bg-surface-coal rounded-2xl flex items-center justify-center mx-auto mb-6">
            <IconUser size={40} className="text-brand-orange" />
          </div>

          {/* Title */}
          <h1 className="text-3xl font-display font-bold text-accent-white mb-4">
            My Account
          </h1>
          <p className="text-accent-silver mb-8">
            Sign in to view your orders, manage your details, and track deliveries.
          </p>

          {/* Action Buttons */}
          <div className="space-y-4">
            <a
              href={shopifyLoginUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary w-full justify-center"
            >
              Sign In
              <IconArrowRight size={18} />
            </a>
            <a
              href={shopifyRegisterUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary w-full justify-center"
            >
              Create Account
            </a>
          </div>

          {/* Benefits */}
          <div className="mt-12 pt-8 border-t border-surface-slate">
            <h2 className="text-sm font-semibold text-accent-white mb-4">
              Account Benefits
            </h2>
            <ul className="space-y-3 text-sm text-accent-silver text-left">
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 bg-brand-orange/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="w-2 h-2 bg-brand-orange rounded-full" />
                </span>
                Track your orders and delivery status
              </li>
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 bg-brand-orange/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="w-2 h-2 bg-brand-orange rounded-full" />
                </span>
                View order history and reorder easily
              </li>
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 bg-brand-orange/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="w-2 h-2 bg-brand-orange rounded-full" />
                </span>
                Save addresses for faster checkout
              </li>
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 bg-brand-orange/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="w-2 h-2 bg-brand-orange rounded-full" />
                </span>
                Get exclusive offers and early access
              </li>
            </ul>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
}
